import { c as _c } from "react/compiler-runtime";
import { Box, Card, CloseButton, Dialog, Flex, Heading, HStack, Icon, Portal, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { UserSignupEmailCaptureForm } from '@src/components/sco/Account/UserSignupEmailCaptureForm';
import { AdvisorsIllustration } from '@src/components/sco/Advisors/AdvisorsLandingIllustrations';
import { ADVISORS_BASE } from '@src/components/sco/Advisors/constants';
import { FREE_WIL_MESSAGE_LIMIT } from '@src/components/sco/Advisors/model/access';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { usePlanPriceLabels, withPrice } from '@src/components/sco/Conversion/controller/usePlanPriceLabels';
import { GenericContentBlockConfig, GenericContentBlocks } from '@src/components/sco/GenericLP/GenericContentBlock';
import GenericFAQ from '@src/components/sco/GenericLP/GenericFAQ';
import GenericHero from '@src/components/sco/GenericLP/GenericHero';
import { GenericHowItWorksItem } from '@src/components/sco/GenericLP/GenericHowItWorks';
import GenericSection from '@src/components/sco/GenericLP/GenericSection';
import { TextLink } from '@src/components/sco/TextLink';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useLoginHref } from '@src/hooks/useLoginHref';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect.tsx';
import { TrackEvent } from '@src/lib/TrackEvent.tsx';
import React, { useCallback, useMemo, useState } from 'react';
import { LuChartLine, LuChevronRight, LuCircleCheck, LuRocket, LuUsers } from 'react-icons/lu';
export { getServerSideProps } from '@src/lib/server-side-rendering.tsx';

/** Pre-V2 public `/advisors` marketing LP. V2 copy lives at `/advisors/v2`. */
const variantKey = 'advisors';
const SECTION_BG = 'colorPalette.bg';

// ─── Page content (edit here) ─────────────────────────────────────────────────

const items: GenericHowItWorksItem[] = [{
  icon: LuRocket,
  text: 'Recruit',
  description: 'Find and onboard the right advisors.'
}, {
  icon: LuUsers,
  text: 'Compensate',
  description: 'Standard agreements, done right.'
}, {
  icon: LuChartLine,
  text: 'Manage',
  description: 'Keep advisors earning their equity.'
}];
const buildMatchingBlocks = (openDiagnostic: () => void): GenericContentBlockConfig[] => [{
  eyebrow: 'Real Advisors',
  heading: 'Real advisors, on demand.',
  paragraph: 'When the question is bigger than a chat, talk to a person. Experienced operators are available right away, with no recruiting, no searching, and no waiting weeks for an introduction.',
  listItems: ['Talk to an expert right away', 'No recruiting, no waiting', 'Office hours, calls, and messaging'],
  primaryCta: {
    label: 'Get Free Startup Advice',
    onClick: openDiagnostic
  },
  visual: <AdvisorsIllustration illustrationIndex={6} />
}, {
  eyebrow: 'Wil.ai',
  heading: 'An advisor who never sleeps.',
  paragraph: 'Wil.ai is trained on thousands of real Founder conversations and the experience of helping over a million startups. Ask anything, any hour, and get a straight answer.',
  listItems: ['Trained on real Founder conversations', 'Straight answers in seconds, 24/7', 'Knows your stage and your context'],
  primaryCta: {
    label: 'Get Free Startup Advice',
    onClick: openDiagnostic
  },
  visual: <AdvisorsIllustration illustrationIndex={7} />
}, {
  eyebrow: 'Deep Expertise',
  heading: 'Deep expertise, not guesses.',
  paragraph: 'Our advisors go deep in the areas that decide whether you make it. Funding, pitch decks, customer acquisition, and product development, from operators who have done the exact thing you are doing.',
  listItems: ['Funding and pitch decks', 'Customer acquisition', 'Product development'],
  primaryCta: {
    label: 'Get Free Startup Advice',
    onClick: openDiagnostic
  },
  visual: <AdvisorsIllustration illustrationIndex={8} />
}, {
  eyebrow: 'Direct Messaging',
  heading: 'Start right now.',
  paragraph: 'Real Advisors are instantly available to answer your in-depth questions through our direct messaging interface. Work several problems at the same time, with the right expert on each one.',
  listItems: ['Message any advisor directly, no calendar required', 'Run several conversations at once', 'Every answer stays in one thread you can revisit'],
  primaryCta: {
    label: 'Get Free Startup Advice',
    onClick: openDiagnostic
  },
  visual: <AdvisorsIllustration illustrationIndex={9} />
}];
const buildFaqItems = (prices: {
  pro: string | null;
  team: string | null;
}) => [{
  question: 'What is Wil.ai?',
  answer: 'An AI advisor trained on thousands of real Founder conversations and fifteen years of helping startups launch. Ask it anything about your startup and get a straight answer in seconds, at any hour.'
}, {
  question: 'When do I talk to a real advisor instead?',
  answer: 'Any time the question is bigger than a chat. Wil.ai points you to the right person on the team, and you can book office hours or message them directly.'
}, {
  question: 'What can advisors actually help with?',
  answer: 'Funding, pitch decks, customer acquisition, and product development are where we go deepest. If it is blocking your startup, it is fair game.'
}, {
  question: 'How fast can I get access?',
  answer: 'Immediately. Create a free account and ask your first question in under a minute. There is nothing to recruit and no introduction to wait for.'
}, {
  question: 'Is there a cost to get started?',
  answer: `Free to start, with your first ${FREE_WIL_MESSAGE_LIMIT} messages included. Unlimited access comes with ${withPrice('Startups Pro', prices.pro)}, and the full advisor team for office hours and 1:1 messaging is ${withPrice('Advisor Team', prices.team)}.`
}];

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AdvisorsLandingSection = () => {
  const $ = _c(23);
  const planPrices = usePlanPriceLabels();
  const activeContent = useActiveContentContext();
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setDiagnosticOpen(true);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const openDiagnostic = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = buildMatchingBlocks(openDiagnostic);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const matchingBlocks = t1;
  let t2;
  if ($[2] !== activeContent) {
    t2 = () => {
      activeContent.setSiteSection("advisors.landing");
      activeContent.setItem({
        __typename: "LandingPage",
        title: "Startup Advisors & Wil.ai",
        description: "The advice you need, the moment you need it. A 24/7 AI advisor blended with real-world experts for funding, pitch decks, customers, and product."
      });
      return () => {
        activeContent.setItem(null);
      };
    };
    $[2] = activeContent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useUniversalEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TrackEvent action="lpViewed" onlyOnScreen={true} traits={{
      variantKey: "advisors-landing"
    }} />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TrackEvent action="lpViewed" onlyOnScreen={true} traits={{
      variantKey
    }} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <GenericHero gradientText="The Moment you Need it." heading="The Advice you Need, The Moment you Need it." subheading="A Perfect Blend of a 24x7 AI with Real World Experts"><AdvisorsSignup /></GenericHero>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <GenericSection background={SECTION_BG} heading="An entire Startup Advisory Board, Instantly." subheading="You now have seasoned Advisors for Funding, Customer Acquisition, Product Development and more."><GenericContentBlocks blocks={matchingBlocks} /></GenericSection>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== planPrices) {
    t8 = buildFaqItems(planPrices);
    $[9] = planPrices;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <GenericFAQ heading="Frequent Questions" items={t8} subheading="Want to know more? Here's some common questions & answers." />;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = event => setDiagnosticOpen(event.open);
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Dialog.Backdrop backdropFilter="blur(10px)" bg="colorPalette.bg/50" />;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [3, 4, 6];
    t13 = [4, 6];
    $[15] = t12;
    $[16] = t13;
  } else {
    t12 = $[15];
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Portal>{t11}<Dialog.Positioner paddingX={t12} paddingY={t13}><Dialog.Content bg="transparent" boxShadow="none" maxWidth="container.lg" overflow="visible" width="full"><Box position="relative"><Dialog.CloseTrigger asChild={true} position="absolute" right={3} top={3} zIndex={2}><CloseButton bg="colorPalette.container" borderColor="colorPalette.border/50" borderRadius="full" borderWidth="1px" size="sm" /></Dialog.CloseTrigger><AdvisorsSignup id={undefined} /></Box></Dialog.Content></Dialog.Positioner></Portal>;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== diagnosticOpen) {
    t15 = <Dialog.Root motionPreset="scale" open={diagnosticOpen} placement="center" scrollBehavior="inside" size="xl" onOpenChange={t10}>{t14}</Dialog.Root>;
    $[18] = diagnosticOpen;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t15 || $[21] !== t9) {
    t16 = <Box color="fg" width="full">{t4}{t5}{t6}{t7}{t9}{t15}</Box>;
    $[20] = t15;
    $[21] = t9;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  return t16;
};
const AdvisorsSignup = t0 => {
  const $ = _c(16);
  const {
    id: t1
  } = t0;
  const id = t1 === undefined ? "signup" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["Nailing your pitch deck", "Raising your round", "Finding and winning customers", "Building the right product"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const HELP_TOPICS = t2 as const;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [{
      value: "1M+",
      label: "Startups"
    }, {
      value: "$20B",
      label: "Raised"
    }, {
      value: "23M",
      label: "Customers"
    }];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const PROOF_STATS = t3 as const;
  const {
    currentUser
  } = useCurrentUser();
  const loginHref = useLoginHref();
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [5, 6, 7];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      base: 1,
      md: 2
    };
    t6 = {
      base: 8,
      md: 7
    };
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["xl", "2xl", "3xl"];
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Heading as="h2" fontSize={t7} fontWeight="semibold" letterSpacing="-0.05em" lineHeight="1.3">Join over a Million Startups<br /><Box as="span" color="orange.fg">Who we've Helped</Box></Heading>;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Text color="fg.subtle" fontSize="sm" fontWeight="bold" letterSpacing="0.1em" textTransform="uppercase">What you can get help with</Text>;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <VStack align="stretch" gap={2.5} width="full">{t9}<VStack align="stretch" gap={2} width="full">{HELP_TOPICS.map(_temp)}</VStack></VStack>;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <VStack align="stretch" gap={5}>{t8}{t10}<Flex align="stretch" bg="bg.subtle" borderColor="border" borderRadius="lg" borderWidth="1px" justify="stretch" overflow="hidden" width="full">{PROOF_STATS.map(_temp2)}</Flex></VStack>;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== currentUser?.isRegistered || $[11] !== loginHref) {
    t12 = <Card.Body padding={t4}><SimpleGrid alignItems="center" columns={t5} gap={t6}>{t11}<VStack align="stretch" gap={4}>{currentUser?.isRegistered ? <Box textAlign="center"><Heading marginY={4}>You're logged in!</Heading><ButtonLink href="/advisors" variant="cta">Access Advisors <Icon as={LuChevronRight} /></ButtonLink></Box> : <VStack align="stretch"><UserSignupEmailCaptureForm containerProps={{
              bg: "colorPalette.card/50",
              width: "full"
            }} hubspotFormKey="scom-advisors-landing" returnToHref={ADVISORS_BASE} socialLoginButtonsProps={{
              buttonLayout: "google-only"
            }} submitButtonProps={{
              children: "Get Free Startup Advice",
              size: "lg"
            }} /><Text color="fg.muted" fontSize="sm" textAlign="center">Already have an account? <TextLink href={loginHref}>Log in</TextLink></Text></VStack>}</VStack></SimpleGrid></Card.Body>;
    $[10] = currentUser?.isRegistered;
    $[11] = loginHref;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== id || $[14] !== t12) {
    t13 = <Card.Root bg="colorPalette.container" borderColor="colorPalette.border/50" borderRadius="2xl" borderWidth="1px" boxShadow="md" id={id} marginX="auto" maxWidth="container.lg" scrollMarginTop={24} variant="outline" width="full">{t12}</Card.Root>;
    $[13] = id;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  return t13;
};
function _temp(topic) {
  return <HStack key={topic} align="center" gap={2.5}><Icon as={LuCircleCheck} boxSize={4} color="orange.fg" flexShrink={0} /><Text color="fg" fontSize={["sm", "md"]} fontWeight="medium">{topic}</Text></HStack>;
}
function _temp2(stat, i) {
  return <Box key={stat.label} borderColor="border" borderLeftWidth={i === 0 ? 0 : "1px"} paddingX={[4, 5]} paddingY={3} textAlign="center" width="full"><Text fontSize={["xl", "2xl"]} fontWeight="black" letterSpacing="-0.02em" lineHeight={1}>{stat.value}</Text><Text color="fg.subtle" fontSize="2xs" letterSpacing="0.08em" marginTop={1} textTransform="uppercase">{stat.label}</Text></Box>;
}